import './Board.css'

import React from 'react'
import Field from './Field'
import Piece from './Piece'
import classNames from 'classnames'
import _ from 'lodash'
import EventsHandler from './EventsHandler'
import { WHITE, BLACK, WHITE_KING, BLACK_KING } from './consts'
import {
  FRAME_PICK_UP,
  FRAME_PUT_DOWN,
  FRAME_MOVE,
  FRAME_CAPTURE,
  FRAME_PROMOTE,
  FRAME_FINISH,
} from './BoardAnimation'

class Board extends React.Component {
  static getDerivedStateFromProps(props, state) {
    return {
      fields: props.fields || state.fields,
      highlights: props.highlights || state.highlights,
      arrows: props.arrows || state.arrows,
      features: Object.assign(
        {
          highlights: true,
          arrows: true,
          edition: true,
          coords: true,
        },
        props.features || {}
      ),
    }
  }

  constructor(props) {
    super(props)

    this._key = 0

    this._arrowColor = 'orange'
    this._piecesRefs = {}
    this._fieldsRefs = {}
    this._lastColor = props.color

    this._eventsHandler = new EventsHandler()
    this._dragPiece = null
    this._dragArrow = null

    this.state = {
      arrow: null,
      arrows: [],
      highlights: {},
      fields: {},
    }
  }

  componentDidMount() {
    this._eventsHandler.bind('keydown')
    this._eventsHandler.bind('keyup')
    this._eventsHandler.enabled = () => !this.props.disabled

    this._eventsHandler.on(null, 'keydown', (e) => this._changeArrowColor(e))
    this._eventsHandler.on(null, 'keyup', (e) => this._changeArrowColor(e))

    this._eventsHandler.on(null, 'mousedown', (e) => {
      if (e.event.target.classList.contains('arrow')) {
        //arrow
        if (e.mouseButton === 2 && this.state.features.arrows) {
          this._dragArrow = e.data
          this._dragArrow.committed = false
        }
      } else if (e.event.target.classList.contains('field')) {
        //field
        const index = this._index(e.data)
        if (this.state.fields[index]) {
          this.props.onSelectPiece(index)
        }
      }
    })

    this._eventsHandler.on(null, 'mouseup', (e) => {
      //commit arrow if edited, no matter which button pressed
      this._commitArrow(e)
    })

    this._eventsHandler.on(null, 'click', (e) => {
      if (e.event.target.classList.contains('field')) {
        //field
        if (this._isPlayableField(e.data)) {
          this._onPlayableFieldClicked(e)
        } else {
          this._onNonPlayableFieldClicked(e)
        }
      } else if (e.event.target.classList.contains('arrow')) {
        //arrow
        if (e.mouseButton === 2 && this.state.features.arrows) {
          const arrows = this.state.arrows.filter((v) => v !== e.data)
          this.setState({ arrows: [...arrows] })
          this.props.onRemoveArrow(e.data)
        }
      }
    })

    this._eventsHandler.on(null, 'dragstart', (e) => {
      if (e.mouseButton === 0) {
        //piece
        if (!this._dragAcceptFrom(this._index(e.data))) {
          return
        }

        let node = this._piecesRefs[e.data]
          ? this._piecesRefs[e.data].current
          : null

        if (node) {
          if (this.props.dragGhost) {
            const parent = node.parentNode
            node = node.cloneNode(true)
            parent.appendChild(node)

            node.classList.add('ghost')
          }

          this._dragPiece = {
            node,
            from: e.data,
          }
          node.classList.add('drag')
        }
      } else if (e.mouseButton === 2) {
        //arrow
        if (!this.state.features.arrows) {
          return
        }

        if (!this._dragArrow) {
          this._createArrow(e)
        } else if (e.element.node.classList.contains('arrow')) {
          this._editArrow(e)
        }
      }
    })

    this._eventsHandler.on(null, 'drag', (e) => {
      if (e.mouseButton === 0) {
        //piece
        if (this._dragPiece) {
          this._dragPiece.node.style.left = e.element.currentPos.x + 'px'
          this._dragPiece.node.style.top = e.element.currentPos.y + 'px'
        }
      } else if (e.mouseButton === 2) {
        //arrow
        if (e.event.target.classList.contains('field')) {
          this._moveArrow(e)
        }
      }
    })

    this._eventsHandler.on(null, 'dragend', (e) => {
      if (e.mouseButton === 0) {
        //piece
        if (this._dragPiece) {
          if (this.props.dragGhost) {
            this._dragPiece.node.parentNode.removeChild(this._dragPiece.node)
          } else {
            this._dragPiece.node.classList.remove('drag')
          }

          const boardIndexFrom = this._index(this._dragPiece.from)
          const boardIndexTo = this._index(e.data)

          if (
            boardIndexFrom !== boardIndexTo &&
            this._isPlayableField(boardIndexTo) &&
            this._dragAcceptTarget(boardIndexFrom, boardIndexTo)
          ) {
            if (this.state.features.edition) {
              this._movePiece(boardIndexFrom, boardIndexTo)
            } else if (this.props.onPieceDragEnd) {
              this.props.onPieceDragEnd(boardIndexFrom, boardIndexTo)
            }
          } else {
            this._redrawPiece(boardIndexFrom)
          }
        }
        this._dragPiece = null
      } else if (e.mouseButton === 2) {
        //arrow
        this._commitArrow(e)
      }
    })
  }

  _dragAcceptFrom(from) {
    return !this.props.dragTargets || !!this.props.dragTargets[from]
  }

  _dragAcceptTarget(from, to) {
    if (!this.props.dragTargets) {
      return true
    }

    return (
      this.props.dragTargets[from] &&
      this.props.dragTargets[from].indexOf(to) > -1
    )
  }

  _createArrow(e) {
    const x = e.element.node.offsetLeft + e.element.node.offsetWidth / 2
    const y = e.element.node.offsetTop + e.element.node.offsetHeight / 2

    this._dragArrow = {
      type: this._arrowColor,
      begin: { x, y },
      end: { x, y },
      committed: false,
      id: this._getKey(),
    }
  }

  _editArrow(e) {
    if (!this.state.features.arrows || !this._dragArrow) {
      return
    }

    const arrows = this.state.arrows.filter((v) => v !== this._dragArrow)
    this._dragArrow.committed = false
    this.setState({
      arrows: [...arrows],
      arrow: { ...this._dragArrow },
    })
  }

  _moveArrow(e) {
    if (!this.state.features.arrows || !this._dragArrow) {
      return
    }

    let x = e.event.target.offsetLeft + e.event.target.offsetWidth / 2
    let y = e.event.target.offsetTop + e.event.target.offsetHeight / 2

    y +=
      y < this._dragArrow.begin.y ? 10 : this._dragArrow.begin.y === y ? 0 : -10
    x +=
      x < this._dragArrow.begin.x ? 10 : this._dragArrow.begin.x === x ? 0 : -10

    if (
      (this._dragArrow.end.x !== x || this._dragArrow.end.y !== y) &&
      this._isPlayableField(e.data)
    ) {
      this._dragArrow.end = { x, y }
      this.setState({ arrow: this._dragArrow })
    }
  }

  _commitArrow(e) {
    if (!this.state.features.arrows || !this._dragArrow) {
      return
    }

    const arrow = this._dragArrow

    if (arrow.end) {
      arrow.committed = true

      const arrows = [...this.state.arrows]

      arrows.push(arrow)
      this.setState({ arrows: [...arrows] })
      this.props.onDrawArrow(arrow)
    }

    this.setState({ arrow: null })
    this._dragArrow = null
  }

  _setPiece(index, piece) {
    const f = { ...this.state.fields }
    f[index] = piece
    this.setState({ fields: f })

    this.props.onSetPiece(index, piece)
  }

  _removePiece(index) {
    const f = { ...this.state.fields }
    delete f[index]
    this.setState({ fields: f })

    this.props.onRemovePiece(index)
  }

  _movePiece(from, to, piece = null) {
    piece = piece || this.state.fields[from]

    if (piece) {
      const f = { ...this.state.fields }
      delete f[from]
      f[to] = piece

      this.setState({ fields: f })

      this.props.onMovePiece(from, to, piece)
    }
  }

  _redrawPiece(index) {
    const piece = this.state.fields[index]

    if (piece) {
      this._removePiece(index)

      setTimeout(() => {
        this._setPiece(index, piece)
      }, 0)
    }
  }

  _changeArrowColor(e) {
    if (!this.state.features.arrows) {
      return
    }

    if (e.altKey) {
      this._arrowColor = 'blue'
    } else if (e.ctrlKey) {
      this._arrowColor = 'red'
    } else if (e.shiftKey) {
      this._arrowColor = 'green'
    } else {
      this._arrowColor = 'orange'
    }

    if (this._dragArrow && this._arrowColor !== this._dragArrow.type) {
      this._dragArrow.type = this._arrowColor
      this.setState({ arrow: { ...this._dragArrow } })
    }
  }

  _getKey() {
    return this._key++
  }

  _onNonPlayableFieldClicked(e) {
    this.setState({
      arrows: [],
      highlights: {},
    })
    this.props.onNonPlayableFieldClicked(this._index(e.data))
  }

  _highlightField(index, color) {
    if (!this.state.features.highlights) {
      return
    }

    const h = { ...this.state.highlights }
    h[index] = color
    this.setState({ highlights: h })

    this.props.onHighlightField(index, color)
  }

  _index(index) {
    return this.props.rotated
      ? this.props.size * this.props.size - index - 1
      : index
  }

  _onPlayableFieldClicked(e) {
    const index = this._index(e.data)

    let piece = this.state.fields[index]

    if (e.mouseButton === 2) {
      if (e.shiftKey) {
        this._highlightField(index, 'green')
      } else if (e.ctrlKey) {
        this._highlightField(index, 'red')
      } else if (e.altKey) {
        this._highlightField(index, 'blue')
      } else if (piece && this.state.features.edition) {
        this._removePiece(index)
      } else {
        this._highlightField(index, false)
      }
    } else if (e.mouseButton === 0) {
      if (!this.state.features.edition) {
        this.props.onPlayableFieldClicked(index)

        return
      }

      if (piece && e.ctrlKey) {
        this._removePiece(index)
        return
      }

      if (piece && e.shiftKey) {
        if (piece === WHITE) {
          this._setPiece(index, WHITE_KING)
        } else if (piece === BLACK) {
          this._setPiece(index, BLACK_KING)
        } else if (piece === WHITE_KING) {
          this._setPiece(index, WHITE)
        } else if (piece === BLACK_KING) {
          this._setPiece(index, BLACK)
        }

        return
      }

      if (piece && e.altKey) {
        if (piece === WHITE) {
          this._setPiece(index, BLACK)
          return
        }

        if (piece === BLACK) {
          this._setPiece(index, WHITE)
          return
        }

        if (piece === WHITE_KING) {
          this._setPiece(index, BLACK_KING)
          return
        }

        if (piece === BLACK_KING) {
          this._setPiece(index, WHITE_KING)
          return
        }
      }

      if (this._lastColor !== this.props.color) {
        piece = this.props.color
      } else {
        if (piece === WHITE) {
          piece = WHITE_KING
        } else if (piece === BLACK) {
          piece = BLACK_KING
        } else if (piece === WHITE_KING) {
          piece = this.props.color === BLACK ? undefined : null
        } else if (piece === BLACK_KING) {
          piece = this.props.color === WHITE ? undefined : null
        } else if (piece === null) {
          piece = this.props.color === WHITE ? BLACK : WHITE
        } else {
          piece = this.props.color
        }
      }

      this._lastColor = this.props.color

      if (e.altKey && piece) {
        if (piece === WHITE) {
          piece = BLACK
        } else if (piece === BLACK) {
          piece = WHITE
        } else if (piece === WHITE_KING) {
          piece = BLACK_KING
        } else if (piece === BLACK_KING) {
          piece = WHITE_KING
        }
      }

      this._setPiece(index, piece)

      this.props.onPlayableFieldClicked(e.data)
    }
  }

  renderPiece(index) {
    const piece = this.state.fields[index]

    if (!piece) {
      return null
    }

    index = this._index(index)

    const row = Math.floor(index / this.props.size)
    const col = index - row * this.props.size

    const top = row * (100 / this.props.size)
    const left = col * (100 / this.props.size)

    const ref = this._piecesRefs[index]
      ? this._piecesRefs[index]
      : React.createRef()
    this._piecesRefs[index] = ref

    return (
      <Piece
        domRef={ref}
        left={left}
        top={top}
        piece={piece}
        key={index}
        index={index}
        style={{ left: left + '%', top: top + '%' }}
        animate={
          this.props.animateMove &&
          this.props.animateMove[0] === this._index(index)
        }
      />
    )
  }

  _isDarkField(index) {
    const row = Math.floor(index / this.props.size)

    // return (this.props.firstFieldWhite && ((index % 2 === 0 && row % 2 === 0) || (index % 2 !== 0 && row % 2 !== 0))) ||
    //        (!this.props.firstFieldWhite && !((index % 2 === 0 && row % 2 === 0) || (index % 2 !== 0 && row % 2 !== 0)));

    return (
      (this.props.firstFieldWhite &&
        ((index % 2 !== 0 && row % 2 === 0) ||
          (index % 2 === 0 && row % 2 !== 0))) ||
      (!this.props.firstFieldWhite &&
        ((index % 2 === 0 && row % 2 === 0) ||
          (index % 2 !== 0 && row % 2 !== 0)))
    )
  }

  _isPlayableField(index) {
    const dark = this._isDarkField(index)

    return (
      (!dark && this.props.playOnWhite) || (dark && !this.props.playOnWhite)
    )
  }

  _getPieceNodeAt(index) {
    return this._piecesRefs[this._index(index)].current
  }

  _getFieldNodeAt(index) {
    return this._fieldsRefs[this._index(index)].current
  }

  async _animateMove() {
    let promote = null
    let from = null
    let to = null
    let pieceNode = null

    let setClass = (className) => {
      clearClass()
      pieceNode.classList.add('animate-' + className)
    }

    let clearClass = () => {
      pieceNode.classList.forEach((oldClass) => {
        if (oldClass.match(/^animate-/)) {
          pieceNode.classList.remove(oldClass)
        }
      })
    }

    const fields = { ...this.state.fields }
    let path = []

    return this.props.animation.run((data) => {
      switch (data.frame.type) {
        case FRAME_PICK_UP:
          from = data.frame.index
          pieceNode = this._getPieceNodeAt(data.frame.index)
          pieceNode.classList.add('drag')
          path.push(data.frame.index)

          break

        case FRAME_MOVE:
          to = data.frame.index[1]
          path.push(to)

          setClass(data.class)
          // eslint-disable-next-line
          const field = this._getFieldNodeAt(to)

          pieceNode.style.left = field.offsetLeft + 'px'
          pieceNode.style.top = field.offsetTop + 'px'
          break

        case FRAME_PUT_DOWN:
          to = data.frame.index
          path.push(to)
          pieceNode.classList.remove('drag')
          break

        case FRAME_CAPTURE:
          this._getPieceNodeAt(data.frame.index).classList.add('captured')
          delete fields[data.frame.index]
          path.push(data.frame.index)

          break

        case FRAME_PROMOTE:
          promote = data.frame.piece
          path.push(data.frame.index)
          break

        case FRAME_FINISH:
          fields[to] = promote || fields[from]
          delete fields[from]

          path = path.filter(
            (value, index, self) => self.indexOf(value) === index
          )

          this.setState({ fields })
          this.props.onAfterMoveAnimated(fields, path)
          break

        default:
          break
      }
    })
  }

  componentDidUpdate() {
    if (this.props.animation) {
      this._animateMove()
    }
  }

  annotateField(index, number) {
    if (this.props.fieldAlphaNotation) {
      index = this.props.rotated
        ? this.props.size * this.props.size - index - 1
        : index
      const alpha = 'abcdefghij'
      const col = index % this.props.size
      const row = Math.floor(index / this.props.size)

      return alpha.charAt(col) + (this.props.size - row)
    }

    return number
  }

  render() {
    const fieldsCount = this.props.size * this.props.size

    let number = this.props.rotated ? fieldsCount / 2 + 1 : 0

    const fields = _.range(fieldsCount).map((index) => {
      const dark = this._isDarkField(index)
      const playable = this._isPlayableField(index)

      number += (!this.props.rotated ? 1 : -1) * (playable ? 1 : 0)

      const ref = this._fieldsRefs[index]
        ? this._fieldsRefs[index]
        : React.createRef()
      this._fieldsRefs[index] = ref

      return (
        <Field
          key={index}
          fieldRef={ref}
          number={
            playable && this.state.features.coords
              ? this.annotateField(index, number)
              : null
          }
          dark={dark}
          highlight={this.state.highlights[this._index(index)]}
          color={
            this.props.fieldColors
              ? this.props.fieldColors[this._index(index)]
              : null
          }
          moved={
            this.props.lastMovePath &&
            this.props.lastMovePath.indexOf(this._index(index)) > -1
          }
          onMouseMove={(e) => {
            this._eventsHandler.handle(e, index)
          }}
          onMouseDown={(e) => {
            this._eventsHandler.handle(e, index)
          }}
          onMouseUp={(e) => {
            this._eventsHandler.handle(e, index)
          }}
        ></Field>
      )
    })

    const pieces = _.range(fieldsCount).map((index) => {
      return this.renderPiece(index)
    })

    const arrow = (arrow) => {
      const x1 = arrow.begin.x
      const y1 = arrow.begin.y
      const x2 = arrow.end ? arrow.end.x : arrow.begin.x
      const y2 = arrow.end ? arrow.end.y : arrow.begin.y

      return (
        <g
          key={
            x1 +
            '-' +
            y1 +
            '-' +
            x2 +
            '-' +
            y2 +
            '-' +
            arrow.type +
            '_' +
            this._getKey()
          }
        >
          <line
            className={classNames('arrow', 'bg', { editing: !arrow.committed })}
            strokeWidth="12"
            strokeLinecap="round"
            markerEnd={'url(#arrowhead-bg)'}
            opacity="1"
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
          ></line>
          <line
            className={classNames('arrow ' + arrow.type, {
              editing: !arrow.committed,
            })}
            strokeWidth="9"
            strokeLinecap="round"
            markerEnd={'url(#arrowhead-' + arrow.type + ')'}
            opacity="1"
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            onContextMenu={(e) => this._eventsHandler.handle(e, arrow)}
            onMouseDown={(e) => this._eventsHandler.handle(e, arrow)}
            onMouseUp={(e) => this._eventsHandler.handle(e, arrow)}
          ></line>
        </g>
      )
    }

    const arrows = this.state.arrows.map(arrow)

    const editing = !!this.state.arrow

    return (
      <div
        tabIndex={0}
        className={classNames('board', 'size-' + this.props.size, {
          disabled: this.props.disabled,
        })}
        onContextMenu={this._eventsHandler.handle}
        onMouseLeave={this._eventsHandler.handle}
      >
        <svg>
          <defs>
            <marker
              id="arrowhead-bg"
              orient="auto"
              markerWidth="4"
              markerHeight="4"
              refX="1.7"
              refY="2.2"
              className={classNames('bg', { editing })}
            >
              <path
                d="M0.03,0.45 V4 L2.7,2.2 Z"
                className={classNames({ editing })}
              ></path>
            </marker>
            <marker
              id="arrowhead-red"
              orient="auto"
              markerWidth="4"
              markerHeight="8"
              refX="2.05"
              refY="2.00"
              className={classNames('red', { editing })}
            >
              <path
                d="M0,0 V4 L3,2 Z"
                className={classNames({ editing })}
              ></path>
            </marker>
            <marker
              id="arrowhead-green"
              orient="auto"
              markerWidth="4"
              markerHeight="8"
              refX="2.05"
              refY="2.00"
              className={classNames('green', { editing })}
            >
              <path
                d="M0,0 V4 L3,2 Z"
                className={classNames({ editing })}
              ></path>
            </marker>
            <marker
              id="arrowhead-blue"
              orient="auto"
              markerWidth="4"
              markerHeight="8"
              refX="2.05"
              refY="2.00"
              className={classNames('blue', { editing })}
            >
              <path
                d="M0,0 V4 L3,2 Z"
                className={classNames({ editing })}
              ></path>
            </marker>
            <marker
              id="arrowhead-orange"
              orient="auto"
              markerWidth="4"
              markerHeight="8"
              refX="2.05"
              refY="2.00"
              className={classNames('orange', { editing })}
            >
              <path
                d="M0,0 V4 L3,2 Z"
                className={classNames({ editing })}
              ></path>
            </marker>
          </defs>
          {arrows}
          {this.state.arrow ? arrow(this.state.arrow) : null}
        </svg>
        {fields}
        {pieces}
        <Piece
          left={0}
          top={0}
          piece={WHITE_KING}
          style={{ visibility: 'hidden' }}
        />
        <Piece
          left={0}
          top={0}
          piece={BLACK_KING}
          style={{ visibility: 'hidden' }}
        />
      </div>
    )
  }
}

export default Board
